import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProjectSettings, getStageSettings } from "@modules/settings";
import { MENU_LIST, SHOW_MENU_OPTIONS } from "@constants";
import { useTranslation } from "react-i18next";
import compareVersions, {
    VERSION_CM_10137_CUSTOM_CONTENT_SCHEMAS,
    VERSION_CM_10269_CUSTOM_CONTENTS,
    VERSION_CM_10929_ASSET_REPLIES,
    VERSION_CM_11777_PROJECT_SETTINGS_ADD_MENU_NAME,
} from "@cores/version";
import { siteMode } from "@cores/siteMode";
import { getSettingsAPI } from "@modules/apis/setting";

const useSettings = ({ type = "", domain, id = "" }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [settingsPending, setSettingsPending] = useState({
        settingData: false,
        projectSettingData: false,
        stageSettingData: false,
    });
    const [resourceSettings, setResourceSettings] = useState({ resource: null, settings: null });
    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);
    const settings = useSelector(({ settings }) => settings);

    const generalSetting = JSON.parse(localStorage.getItem(`generalSetting`));

    const isCardViewInfo =
        JSON.parse(localStorage.getItem(`${domain}ViewInfo${id ? `${id}` : ""}`))?.isCardView ??
        (generalSetting?.consoleDefaultListLayout ? generalSetting?.consoleDefaultListLayout === "GRID" : "LIST");

    const localeLanguage = localStorage.getItem(`i18nLng`) ?? generalSetting?.consoleDefaultLanguage;

    const [isCardView, setIsCardView] = useState(isCardViewInfo);
    const [i18nLng, setI18nLng] = useState(localeLanguage);

    const updateIsCardView = (value) => {
        const originData = JSON.parse(localStorage.getItem(`${domain}ViewInfo${id}`)) ?? {};
        localStorage.setItem(`${domain}ViewInfo${id}`, JSON.stringify({ ...originData, isCardView: value }));
        setIsCardView(value);
    };

    const updateLocaleLanguage = (value) => {
        localStorage.setItem(`i18nLng`, value);
        setI18nLng(value);
    };

    const updateGeneralSettings = (value) => {
        const originGeneralSetting = JSON.parse(localStorage.getItem("generalSetting")) ?? {};
        localStorage.setItem("generalSetting", JSON.stringify({ ...originGeneralSetting, ...value }));
    };

    const getSettingData = useCallback(
        async (resource, key) => {
            try {
                setSettingsPending((prev) => ({ ...prev, settingData: true }));
                let params = {
                    resource: resource,
                };
                if (key) params.key = key;
                const response = await getSettingsAPI(stage.id, stage.endpoint, project.id, params);
                setResourceSettings({ resource, settings: response.data });
                return response.data;
            } catch (e) {
                console.log(e);
                setResourceSettings({ resource, settings: {} });
            } finally {
                setSettingsPending((prev) => ({ ...prev, settingData: false }));
            }
        },
        [stage.id, stage.endpoint, project.id],
    );

    const navigatorMenus = useMemo(() => {
        const menus = {};

        if (!settings?.project) return null;

        const isCms = siteMode.get() === "CMS";
        const isCustomMenu = settings?.project?.MenuName?.selectMenu?.value === "custom";
        const USABLE_MENUS_IN_NOT_DEFAULT_MENU = [
            MENU_LIST.DASHBOARD,
            MENU_LIST.ASSETS,
            MENU_LIST.STORAGES,
            MENU_LIST.COLLECTIONS,
        ];

        SHOW_MENU_OPTIONS.filter(
            (menu) => !(isCustomMenu || isCms) || USABLE_MENUS_IN_NOT_DEFAULT_MENU.includes(menu.id),
        )?.forEach((menu, index) => {
            const computeMenuFlag = () => {
                if (
                    !settings?.project?.General?.showMenuOptions ||
                    parseInt(settings?.project?.General?.showMenuOptions?.value) === 0
                )
                    return true;
                return parseInt(settings?.project?.General.showMenuOptions.value) & menu.value;
            };
            menus[menu.id] = {
                id: menu.id,
                name: menu.name,
                visible: isCustomMenu ? true : computeMenuFlag() > 0,
            };
        });
        return menus;
    }, [settings?.project]);

    const navigatorMenusName = useMemo(() => {
        if (compareVersions(stage.version, VERSION_CM_11777_PROJECT_SETTINGS_ADD_MENU_NAME) >= 0)
            return settings?.project?.MenuName;
        return true;
    }, [stage, settings?.project]);

    const getNavigatorMenuName = useCallback(
        (key) => {
            if (!settings?.project || !settings?.project?.MenuName?.changeName?.value)
                return t(`common::label::${key}`);
            const menus = JSON.parse(settings?.project?.MenuName?.changeName?.value);
            return menus?.[key] ?? t(`common::label::${key}`);
        },
        [settings?.project],
    );

    const domainStatus = useMemo(() => {
        if (!stage) return {};
        const isCustomContentsDisabled =
            compareVersions(stage.version, VERSION_CM_10137_CUSTOM_CONTENT_SCHEMAS) < 0 ||
            compareVersions(stage.version, VERSION_CM_10269_CUSTOM_CONTENTS) < 0 ||
            stage?.extensions?.CUSTOM_CONTENTS_OFF === "true";

        const projectSettingShowCommentOptions = settings?.project?.General?.showCommentOptions?.value
            ? JSON.parse(settings.project.General.showCommentOptions.value)
            : {};

        const isCommentsDisabled =
            compareVersions(stage.version, VERSION_CM_10929_ASSET_REPLIES) < 0 ||
            stage?.extensions?.COMMENTS_OFF === "true" ||
            projectSettingShowCommentOptions?.[domain] === false;

        return {
            COMMENTS_OFF: isCommentsDisabled,
            CUSTOM_CONTENTS_OFF: isCustomContentsDisabled,
        };
    }, [stage, domain, settings?.project?.General]);

    const getReturnProps = (type) => {
        const props = {
            getSettingData,
            settings,
            resourceSettings,
            settingsPending,
        };
        switch (type) {
            case "General":
                return {
                    ...props,
                    isCardView,
                    i18nLng,
                    navigatorMenus,
                    updateIsCardView,
                    updateLocaleLanguage,
                    updateGeneralSettings,
                };
            case "MenuName":
                return {
                    ...props,
                    navigatorMenusName,
                    getNavigatorMenuName,
                };
            case "DomainStatus":
                return { ...props, domainStatus };

            default:
                return props;
        }
    };

    return getReturnProps(type);
};

export default useSettings;
